import { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DataGrowthDashboard, isEnvTypePROD } from '../../service/config';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { checkRole } from '../../utils/roleUtils';
import { useFinOpsTab } from 'Context/contexts/FinOpsTabContext';
import { FilterIcon } from 'components/CommonIcons/Icons';
import HeaderTabs from './HeaderTabs';
import AutoRefresh from '../UnifiedAlerts/AutoRefresh/AutoRefresh';
import CommonAutoRefresh from 'components/Widgets/AutoRefresh/AutoRefresh';
import DateTimeRangeFilter from 'components/Dropdown/DateTimeRangeFilter/DateTimeRangeFilter';
import DateFilterDropdown from 'components/Dropdown/DateFilter';
import DataEstateDateFilter from 'components/Dropdown/DataEstateDateFilter';
import SearchField from 'components/Common/SearchField';
import { FINOPS_EXEC_COST_ROLES, FINOPS_USER_ROLES, FINOPS_ALL_ROLES } from 'components/FinOpsExecutive/finopsRoles';
import { TIMEZONE_MAP } from 'utils/common-util';
import moment from 'moment';
import { parseStoredIbpDate, syncIbpDateRangeFromStorage } from 'utils/ibpDateRange';
const ssoLoginLocalStore = localStorage.getItem('sso-login');
const ssoLogin = ssoLoginLocalStore ? ssoLoginLocalStore === 'true' : process.env.REACT_APP_SSO_LOGIN === 'true';
const sessionCookie = 'id_token';

const PRODUCT_CATALOG_TABS = [
	{
		label: 'Overview',
		path: '/productCatalog/overview',
		activeMatch: (p: string) => p.includes('/productCatalog/overview')
	},
	{
		label: 'Data Quality & Risk',
		path: '/productCatalog/data-quality-and-risk',
		activeMatch: (p: string) => p.includes('/productCatalog/data-quality-and-risk')
	},
	{
		label: 'Renewals',
		path: '/productCatalog/renewals',
		activeMatch: (p: string) => p.includes('/productCatalog/renewals')
	},
];

const PRODUCT_SUCCESS_DASHBOARD_TABS = [
	{
		label: 'User Adoption',
		path: '/productSuccessDashboard/userAdoption',
		activeMatch: (p: string) => p.includes('/productSuccessDashboard/userAdoption')
	},
	{
		label: 'About PepWise',
		path: '/productSuccessDashboard/aboutPepwise',
		activeMatch: (p: string) => p.includes('/productSuccessDashboard/aboutPepwise')
	},
];

const OPS_PULSE_FIXED_OPTIONS = [
	{ val: '5m', label: 'Last 5 Mins' },
	{ val: '10m', label: 'Last 10 Mins' },
	{ val: '15m', label: 'Last 15 Mins' },
	{ val: '30m', label: 'Last 30 Mins' },
	{ val: '1h', label: 'Last 1 Hour' },
	{ val: 'today', label: 'Today' },
	{ val: '24h', label: 'Last 24 Hrs' },
	{ val: '7d', label: 'Last 7 Days' },
	{ val: '30d', label: 'Last 30 Days' },
	{ val: '90d', label: 'Last 90 Days' },
	{ val: 'more_than_90_days', label: 'Older Than 90 Days' },

];

const DATA_RECON_FIXED_OPTIONS = [
	{ val: 'today', label: 'Today' },
	{ val: '24h', label: 'Last 24 Hrs' },
	{ val: '7d', label: 'Last 7 Days' },
	{ val: '30d', label: 'Last 30 Days' },
	{ val: '90d', label: 'Last 90 Days' },
];

// Helper hook for SearchField state management
const useSearchField = (initialText = '') => {
	const [searchText, setSearchText] = useState(initialText);
	const [isSearching, setIsSearching] = useState(false);
	const [isClearingIcon, setIsClearingIcon] = useState(false);

	const reset = () => {
		setSearchText('');
		setIsSearching(false);
		setIsClearingIcon(false);
	};

	return {
		searchText,
		setSearchText,
		isSearching,
		setIsSearching,
		isClearingIcon,
		setIsClearingIcon,
		reset,
	};
};

const getDateRangeStoragePrefix = (
	isDataReconPage: boolean | undefined,
	isIbpPage: boolean,
	isDataAssetsPage: boolean = false
) => {
	if (isDataReconPage) return 'dataRecon';
	if (isIbpPage) return 'IBP';
	if (isDataAssetsPage) return 'dataAssets';
	return 'pipelineRunStatus';
};

const getDataReconDateRangeStoragePrefix = (tab: string) =>
	tab === 'master' ? 'dataReconMaster' : 'dataReconTransactional';

const TabPageHeader = () => {
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const [controller] = usePepwiseAuthController();
	const { role, capabilities } = controller;
	const isIbpPage = location.pathname ? location.pathname.includes('/ibp-dashboard') || location.pathname.includes('/ibpDashboard') : false;
	const isDataReconPage = location.pathname?.includes('/data-pipeline/executiveOps/pepsellPepIrisRecon');
	const selectedDataReconTab = searchParams.get('reconTab') === 'master' ? 'master' : 'transactional';
	const getDataReconTabPath = (tab: string) => {
		const params = new URLSearchParams(location.search);
		params.set('reconTab', tab);
		return `${location.pathname}?${params.toString()}`;
	};
	
	// Resource mapping cost summary detail view flag
	const isDetailViewActive = new URLSearchParams(location.search).get('detailView') === 'true';

	const isMetadataUpdatesPage = location.pathname.includes('/dataOps/pipelineRunStatus/metadataUpdates');
	const isDataAssetsPage = location.pathname.includes('/dataOps/integratedJobMonitoring/dataAssets');

	// SearchField state for global usage across multiple pages
	const globalSearch = useSearchField();

	// Role-based conditions
	const isSystemUserAllowed = checkRole(role, ['Platform Operations Admin/SRE', 'PepWise System User']);
	const isMetadataUpdatesAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
	const isProgramExecutive = role?.length === 1 && role.every((r: any) => r === 'Program Executive');

	const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
		const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
		return saved ? Number(saved) : 5;
	});

	const handleAutoRefreshChange = (interval: number | null) => {
		setAutoRefreshInterval(interval);
		sessionStorage.setItem('unifiedAlertsAutoRefresh', String(interval === null ? 0 : interval));
	};

	const [autoRefreshPipelineInterval, setAutoRefreshPipelineInterval] = useState<number | null>(() => {
		const val = `${getDateRangeStoragePrefix(isDataReconPage, isIbpPage, isDataAssetsPage)}AutoRefresh`;
		const saved = sessionStorage.getItem(val);
		if (saved === null) return 5;
		const parsed = Number(saved);
		return parsed > 0 ? parsed : null;
	});

	const dispatchSessionStorageUpdated = () => {
		if (isIbpPage) {
			window.dispatchEvent(new Event('ibp-session-storage-updated'));
		}
		if (isDataReconPage) {
			window.dispatchEvent(new Event('dataReconSessionStorageUpdated'));
		}
		if (isDataAssetsPage) {
			window.dispatchEvent(new Event('dataAssetsSessionStorageUpdated'));
		} else if (location.pathname.includes('/dataOps/integratedJobMonitoring')) {
			window.dispatchEvent(new Event('pipelineStatusSessionStorageUpdated'));
		}
	};

	const handlePipelineAutoRefreshChange = (interval: number | null) => {
		setAutoRefreshPipelineInterval(interval);
		const val = `${getDateRangeStoragePrefix(isDataReconPage, isIbpPage, isDataAssetsPage)}AutoRefresh`;
		sessionStorage.setItem(val, String(interval === null ? 0 : interval));
		dispatchSessionStorageUpdated();
	};

	const { filterMonth, setFilterMonth, hasFilters, onFiltersOpen } = useFinOpsTab();
	const finopsTab = new URLSearchParams(location.search).get('tab');
	const finopsShowControls = finopsTab !== '2' && finopsTab !== '1';
	const finopsShowDateRefresh = finopsTab !== '2' && finopsTab !== '1' && window.location.pathname !== '/finopsExecutive/cloud-spend-report' && window.location.pathname !== '/finopsExecutive/pre-aop-2027' && window.location.pathname !== '/finopsExecutive/edf-cost-overview' && window.location.pathname !== '/finopsExecutive/2026-dissection' && window.location.pathname !== '/finopsExecutive/edf-cost-overview' && !window.location.pathname.includes('/D&AIXOps/finopsMetrics/insightView');


	const [finopsDateRangeType, setFinopsDateRangeType] = useState(() => sessionStorage.getItem('finopsDateRangeType') || 'today');
	const [finopsCustomStartDate, setFinopsCustomStartDate] = useState<Date | null>(() => {
		const val = sessionStorage.getItem('finopsStartDate');
		return val ? new Date(val) : null;
	});
	const [finopsCustomEndDate, setFinopsCustomEndDate] = useState<Date | null>(() => {
		const val = sessionStorage.getItem('finopsEndDate');
		return val ? new Date(val) : null;
	});

	const handleFinOpsDateRangeChange = (type: string, start?: Date, end?: Date) => {
		setFinopsDateRangeType(type);
		sessionStorage.setItem('finopsDateRangeType', type);
		setFilterMonth(type);
		if (type === 'custom' && start && end) {
			setFinopsCustomStartDate(start);
			setFinopsCustomEndDate(end);
			sessionStorage.setItem('finopsStartDate', start.toISOString());
			sessionStorage.setItem('finopsEndDate', end.toISOString());
		} else {
			setFinopsCustomStartDate(null);
			setFinopsCustomEndDate(null);
			sessionStorage.removeItem('finopsStartDate');
			sessionStorage.removeItem('finopsEndDate');
		}
	};

	const dateRangeStoragePrefix = isDataReconPage
		? getDataReconDateRangeStoragePrefix(selectedDataReconTab)
		: getDateRangeStoragePrefix(isDataReconPage, isIbpPage, isDataAssetsPage);
	const [dateRangeType, setDateRangeType] = useState(
		() => sessionStorage.getItem(`${dateRangeStoragePrefix}DateRangeType`) || (selectedDataReconTab === 'master' ? 'today' : '7d')
	);
	const [customStartDate, setCustomStartDate] = useState<Date | null>(() => {
		const val = sessionStorage.getItem(`${dateRangeStoragePrefix}StartDate`);
		if (!isIbpPage) return val ? new Date(val) : null;
		return parseStoredIbpDate(val, 'start');
	});
	const [customEndDate, setCustomEndDate] = useState<Date | null>(() => {
		const val = sessionStorage.getItem(`${dateRangeStoragePrefix}EndDate`);
		if (!isIbpPage) return val ? new Date(val) : null;
		return parseStoredIbpDate(val, 'end');
	});

	// Sync header with IBP sessionStorage when navigating forward to IBP pages
	useEffect(() => {
		if (!isIbpPage) return;

		const syncFromStorage = () => {
			syncIbpDateRangeFromStorage(setDateRangeType, setCustomStartDate, setCustomEndDate);
		};

		const onApplied = () => syncFromStorage();
		const onStorage = (ev: StorageEvent) => {
			if (ev.key && (ev.key.includes('IBPDateRange') || ev.key.includes('IBPStartDate') || ev.key.includes('IBPEndDate'))) {
				syncFromStorage();
			}
		};

		window.addEventListener('IBPDateRangeApplied', onApplied as EventListener);
		window.addEventListener('ibp-session-storage-updated', onApplied as EventListener);
		window.addEventListener('storage', onStorage as EventListener);

		// initial sync
		syncFromStorage();

		return () => {
			window.removeEventListener('IBPDateRangeApplied', onApplied as EventListener);
			window.removeEventListener('ibp-session-storage-updated', onApplied as EventListener);
			window.removeEventListener('storage', onStorage as EventListener);
		};
	}, [isIbpPage]);

	const [pewsDateRangeType, setPewsDateRangeType] = useState(() => sessionStorage.getItem('unifiedAlertsDateRangeType') || '90d');
	const [pewsCustomStartDate, setPewsCustomStartDate] = useState(() => {
		const val = sessionStorage.getItem('unifiedAlertsStartDate');
		return val ? new Date(val) : null;
	});
	const [pewsCustomEndDate, setPewsCustomEndDate] = useState(() => {
		const val = sessionStorage.getItem('unifiedAlertsEndDate');
		return val ? new Date(val) : null;
	});

	const handlePewsDateRangeChange = (type: string, start?: Date, end?: Date) => {
		// Ops Pulse: restrict custom date selection to last 90 days → today
		const today = new Date();
		today.setHours(23, 59, 59, 999);
		const minDate = new Date();
		minDate.setDate(minDate.getDate() - 90);
		minDate.setHours(0, 0, 0, 0);

		if (type === 'custom' && start && end) {
			const clampedStart = start < minDate ? minDate : start;
			const clampedEnd = end > today ? today : end;
			setPewsDateRangeType(type);
			sessionStorage.setItem('unifiedAlertsDateRangeType', type);
			setPewsCustomStartDate(clampedStart);
			setPewsCustomEndDate(clampedEnd);
			sessionStorage.setItem('unifiedAlertsStartDate', clampedStart.toISOString());
			sessionStorage.setItem('unifiedAlertsEndDate', clampedEnd.toISOString());
		} else if (type === 'more_than_90_days') {
			const endDate = end ?? new Date();
			endDate.setHours(23, 59, 59, 999);
			setPewsDateRangeType(type);
			sessionStorage.setItem('unifiedAlertsDateRangeType', type);
			setPewsCustomStartDate(null);
			setPewsCustomEndDate(endDate);
			sessionStorage.removeItem('unifiedAlertsStartDate');
			sessionStorage.setItem('unifiedAlertsEndDate', endDate.toISOString());
		} else {
			setPewsDateRangeType(type);
			sessionStorage.setItem('unifiedAlertsDateRangeType', type);
			setPewsCustomStartDate(null);
			setPewsCustomEndDate(null);
			sessionStorage.removeItem('unifiedAlertsStartDate');
			sessionStorage.removeItem('unifiedAlertsEndDate');
		}
		window.dispatchEvent(new Event('unifiedAlertsDateRangeChanged'));
	};



	useEffect(() => {
		const handleHydrated = () => {
			setPewsDateRangeType(sessionStorage.getItem('unifiedAlertsDateRangeType') || '24h');
			const start = sessionStorage.getItem('unifiedAlertsStartDate');
			const end = sessionStorage.getItem('unifiedAlertsEndDate');
			setPewsCustomStartDate(start ? new Date(start) : null);
			setPewsCustomEndDate(end ? new Date(end) : null);
		};

		window.addEventListener('unifiedAlertsDateRangeHydrated', handleHydrated);
		return () => window.removeEventListener('unifiedAlertsDateRangeHydrated', handleHydrated);
	}, []);

	const [dataEstateFilterMonth, setDataEstateFilterMonth] = useState<string>('0M');
	const [dataEstateCustomMonth, setDataEstateCustomMonth] = useState<string>('');

	const handleDataEstateDateChange = (selectedDate: string, isCustomDate?: boolean) => {
		setDataEstateFilterMonth(selectedDate);
		sessionStorage.setItem('dataEstateFilterMonth', selectedDate);
		if (isCustomDate) {
			setDataEstateCustomMonth(selectedDate);
			sessionStorage.setItem('dataEstateCustomMonth', selectedDate);
		} else {
			setDataEstateCustomMonth('');
			sessionStorage.removeItem('dataEstateCustomMonth');
		}
		window.dispatchEvent(new CustomEvent('dataEstateDateChanged', { detail: { selectedDate, isCustomDate } }));
	};

	useEffect(() => {
		globalSearch.reset();
		if (location.pathname.includes('/dataestate/majorIncident')) {
			setDataEstateFilterMonth('0Y');
			setDataEstateCustomMonth('');
			sessionStorage.setItem('dataEstateFilterMonth', '0Y');
			sessionStorage.removeItem('dataEstateCustomMonth');
		} else if (location.pathname.includes('/dataestate/queriessummary') || location.pathname.includes('/dataestate/biApplicationSummary') || (location.pathname?.includes('/kpiFramework/configs/') && !location.pathname?.includes('/kpiFramework/configs/new'))) {
			setDataEstateFilterMonth('0M');
			setDataEstateCustomMonth('');
			sessionStorage.setItem('dataEstateFilterMonth', '0M');
			sessionStorage.removeItem('dataEstateCustomMonth');
		}
		if (location.pathname.includes('/dataOps/integratedJobMonitoring') || isIbpPage || isDataReconPage) {
			const pageKey = getDateRangeStoragePrefix(isDataReconPage, isIbpPage, isDataAssetsPage);
			const saved = sessionStorage.getItem(`${pageKey}AutoRefresh`);
			if (saved === null) {
				setAutoRefreshPipelineInterval(5);
			} else {
				const parsed = Number(saved);
				setAutoRefreshPipelineInterval(parsed > 0 ? parsed : null);
			}
			if (!isDataReconPage) {
				const savedRangeType = sessionStorage.getItem(`${pageKey}DateRangeType`);
				const rangeType = savedRangeType || 'today';
				const savedStartDate = sessionStorage.getItem(`${pageKey}StartDate`);
				const savedEndDate = sessionStorage.getItem(`${pageKey}EndDate`);

				setDateRangeType(rangeType);
				setCustomStartDate(savedStartDate ? new Date(savedStartDate) : null);
				setCustomEndDate(savedEndDate ? new Date(savedEndDate) : null);
				if (!savedRangeType) sessionStorage.setItem(`${pageKey}DateRangeType`, rangeType);
				if (!sessionStorage.getItem(`${pageKey}DateRangeTimeZone`)) {
					sessionStorage.setItem(`${pageKey}DateRangeTimeZone`, 'CST');
				}
			}
		}
	}, [location.pathname]);

	useEffect(() => {
		if (!isDataReconPage) return;

		const defaultRange = selectedDataReconTab === 'master' ? 'today' : '7d';
		const prefix = getDataReconDateRangeStoragePrefix(selectedDataReconTab);
		const savedRange = sessionStorage.getItem(`${prefix}DateRangeType`);
		const nextRange = savedRange || defaultRange;
		const savedStartDate = sessionStorage.getItem(`${prefix}StartDate`);
		const savedEndDate = sessionStorage.getItem(`${prefix}EndDate`);

		setDateRangeType(nextRange);
		setCustomStartDate(savedStartDate ? new Date(savedStartDate) : null);
		setCustomEndDate(savedEndDate ? new Date(savedEndDate) : null);
		if (!savedRange) sessionStorage.setItem(`${prefix}DateRangeType`, defaultRange);
	}, [isDataReconPage, selectedDataReconTab]);

	useEffect(() => {
		if (isDataReconPage || isIbpPage) return; 

		const syncFromDashboard = () => {
			const prefix = getDateRangeStoragePrefix(false, false, isDataAssetsPage);
			const type = sessionStorage.getItem(`${prefix}DateRangeType`) || 'today';
			const startStr = sessionStorage.getItem(`${prefix}StartDate`);
			const endStr = sessionStorage.getItem(`${prefix}EndDate`);
			setDateRangeType(type);
			setCustomStartDate(startStr ? new Date(startStr) : null);
			setCustomEndDate(endStr ? new Date(endStr) : null);
		};

		window.addEventListener('pipelineStatusDateRangeChanged', syncFromDashboard);
		return () => window.removeEventListener('pipelineStatusDateRangeChanged', syncFromDashboard);
	}, [isDataReconPage, isIbpPage]);

	const handleDateRangeChange = (type: string, start?: Date, end?: Date) => {
		setDateRangeType(type);
		console.log('Selected Date Range Type:', type, start, end);
		const val = isDataReconPage
			? getDataReconDateRangeStoragePrefix(selectedDataReconTab)
			: getDateRangeStoragePrefix(isDataReconPage, isIbpPage, isDataAssetsPage);
		sessionStorage.setItem(`${val}DateRangeType`, type);
		if (type === 'custom' && start && end) {
			setCustomStartDate(start);
			setCustomEndDate(end);
			if (val === 'IBP') {
				// store dates as YYYY-MM-DD in IBP timezone to avoid UTC shifts
				const tz = TIMEZONE_MAP.CST || 'America/Chicago';
				sessionStorage.setItem(`${val}StartDate`, moment(start).tz(tz).format('YYYY-MM-DD'));
				sessionStorage.setItem(`${val}EndDate`, moment(end).tz(tz).format('YYYY-MM-DD'));
			} else {
				sessionStorage.setItem(`${val}StartDate`, start.toISOString());
				sessionStorage.setItem(`${val}EndDate`, end.toISOString());
			}
		} else {
			setCustomStartDate(null);
			setCustomEndDate(null);
			sessionStorage.removeItem(`${val}StartDate`);
			sessionStorage.removeItem(`${val}EndDate`);
		}
		dispatchSessionStorageUpdated();
	};

	return (

		<>

			{!location.pathname?.includes('platformCostObs') &&

				location.pathname !== DataGrowthDashboard &&

				location.pathname !== '/databricks/summary/dashboard' &&

				location.pathname !== '/databricks/resource/dashboard' &&

				location.pathname !== '/databricks/jobs/dashboard' &&

				location.pathname !== '/databricks/costSummary/dashboard' &&

				location.pathname !== '/databricks/governance/dashboard' &&

				location.pathname !== '/databricks/infrastructure/dashboard' &&

				!location.pathname?.includes('platformHealth/summary') &&

				location.pathname !== '/powerBI/userActivity/dashboard' &&
				location.pathname !== '/powerBI/userActivity/reports' &&
				location.pathname !== '/powerBI/userActivity/users' &&

				// location.pathname !== '/powerBI/capacityObservability/dashboard' &&

				// location.pathname !== '/powerBI/gatewayObservability/dashboard' &&

				// location.pathname !== '/powerBI/adoptionMetrics/dashboard' &&

				// location.pathname !== '/powerBI/dataset/dashboard' &&

				// location.pathname !== '/powerBI/usage/dashboard' &&

				// location.pathname !== '/powerBI/datagrowth/dashboard' &&

				// location.pathname !== '/powerBI/infrastructure/dashboard' &&

				location.pathname !== '/dataManagementFramework/healthSummary/dashboard' &&

				location.pathname !== '/dataManagementFramework/Catalogues/dashboard' &&

				location.pathname !== '/verificationValidation/dashboard' &&

				location.pathname !== '/verificationValidation/powerbi/dashboard' &&

				!location.pathname?.includes('/tableau') &&

				!location.pathname?.includes('/productCatalog') &&

				!location.pathname?.includes('/productSuccessDashboard') &&

				!location.pathname?.includes('/machineLearning') &&

				!location.pathname?.includes('/verificationValidation') &&

				!location.pathname?.includes('/presto') &&

				!location.pathname?.includes('/dataGrowth') &&

				!location.pathname?.includes('/controlsCompliance') &&

				!location.pathname?.includes('/ibp') &&

				!location.pathname?.includes('/databricks') &&

				!location.pathname?.includes('/dataestate') &&

				!location.pathname?.includes('/finops') &&

				!location.pathname?.includes('/data-pipeline') &&

				!location.pathname?.includes('/mdip') &&

				!location.pathname?.includes('/alertFramework') &&

				!location.pathname?.includes('/alert-details') &&

				!location.pathname?.includes('/kpiFramework') &&

				!location.pathname?.includes('/D&AIXOps') &&

				!location.pathname?.includes('/DAIXOps') &&

				!location.pathname?.includes('/enterprisePlatform') &&

				!location.pathname?.includes('/feedback') &&
				!location.pathname?.includes('/demo-dashboard') &&
				!(location.pathname?.includes('/creds')) &&
				!location.pathname?.includes('/dataops') &&
				!location.pathname?.includes('/dataOps/integratedJobMonitoring') &&
				!location.pathname?.includes('/dataOps/crossFlowNavigator') &&
				!location.pathname?.includes('/dashboard/xops-intelligence') &&
				isSystemUserAllowed && (

					<>

						<HeaderTabs

							items={[

								{

									label: 'Platform Executive',

									path: '/dashboard/business',

									activeMatch: (p) => p.includes('/dashboard/business')

									// capability: 'cap.dashboard.platformExecutive'

								},

								{

									label: 'Jobs',

									path: '/jobs',

									activeMatch: (p) => p.includes('/jobs')

								},

								{

									label: 'Job Executions',

									path: '/jobExecutions',

									activeMatch: (p) => p.includes('/jobExecutions')

								},

								{

									label: 'Job Schedules',

									path: '/jobSchedules',

									activeMatch: (p) => p.includes('/jobSchedules')

								},

								{

									label: 'Infrastructure',

									path: '/job/Infrastructure',

									activeMatch: (p) => p.includes('/job/Infrastructure')

								}

							]}

							// capabilities={capabilities}

							left={75}

							right={15}

							zIndex={10}

							topPadding={1}

							showDivider

							// activeColorToken="text.focus"

							// inactiveColorToken="text.main"

							fixed

						/>

						<Box sx={{ height: 56 }} />

					</>

				)}

			{/* Databricks obs sidebar */}

			{(location.pathname === '/databricks/summary/dashboard' ||

				location.pathname === '/databricks/resource/dashboard' ||

				location.pathname === '/databricks/jobs/dashboard' ||

				location.pathname === '/databricks/costSummary/dashboard' ||

				location.pathname === '/databricks/governance/dashboard' ||

				location.pathname === '/databricks/infrastructure/dashboard' ||

				location.pathname?.includes('/databricks/observation')) && (

					<>

						<HeaderTabs

							items={[

								{

									label: 'Summary',

									path: '/databricks/summary/dashboard',

									activeMatch: (p) => p.includes('/databricks/summary/dashboard')

								},

								{

									label: 'Resource Optimization',

									path: '/databricks/resource/dashboard',

									activeMatch: (p) => p.includes('/databricks/resource/dashboard')

								},

								{

									label: 'Jobs',

									path: '/databricks/jobs/dashboard',

									activeMatch: (p) => p.includes('/databricks/jobs/dashboard')

								},

								{

									label: 'Cost Summary',

									path: '/databricks/costSummary/dashboard',

									activeMatch: (p) => p.includes('/databricks/costSummary/dashboard')

								},

								{

									label: 'Governance',

									path: '/databricks/governance/dashboard',

									activeMatch: (p) => p.includes('/databricks/governance/dashboard')

								},

								{

									label: 'Infrastructure',

									path: '/databricks/infrastructure/dashboard',

									activeMatch: (p) => p.includes('/databricks/infrastructure/dashboard')

								}

							]}

							// capabilities={capabilities} // uncomment if you want capability gating

							left={75}

							right={15}

							zIndex={10}

							topPadding={1}

							showDivider

							// activeColorToken="text.focus"

							// inactiveColorToken="text.main"

							fixed

						/>

						<Box sx={{ height: 56 }} />

					</>

				)}

			{/* Tableau obs sidebar */}

			{location.pathname?.includes('/tableau') && isSystemUserAllowed && (

				<>

					<HeaderTabs

						items={[
							{
								label: 'Licenses',
								path: '/tableau/admin/dashboard',
								activeMatch: (p) => p.includes('/tableau/admin/dashboard')
							},
							{
								label: 'Reports',
								path: '/tableau/admin/reports',
								activeMatch: (p) => p.includes('/tableau/admin/reports')
							},
							{
								label: 'Cost',
								path: '/tableau/admin/cost',
								activeMatch: (p) => p.includes('/tableau/admin/cost')
							}
						]}
						// capabilities={capabilities} // uncomment if you want capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						// activeColorToken="text.focus"
						// inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}
			{/* PowerBI obs sidebar */}
			{(
				// location.pathname === '/powerBI/capacity/dashboard' ||
				// location.pathname === '/powerBI/capacityObservability/dashboard' ||
				// location.pathname === '/powerBI/gatewayObservability/dashboard' ||
				// location.pathname === '/powerBI/adoptionMetrics/dashboard' ||
				// location.pathname === '/powerBI/dataset/dashboard' ||
				// location.pathname === '/powerBI/usage/dashboard' ||
				// location.pathname === '/powerBI/datagrowth/dashboard' || // <- keep
				// location.pathname === '/powerBI/infrastructure/dashboard' ||
				location.pathname.startsWith('/powerBI/userActivity')) && (
					<>
						<HeaderTabs
							items={[
								// {
								// 	label: 'Capacity',
								// 	path: '/powerBI/capacity/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/capacity/dashboard')
								// },
								// {
								// 	label: 'Dataset',
								// 	path: '/powerBI/dataset/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/dataset/dashboard')
								// },
								// {
								// 	label: 'Usage & Governance',
								// 	path: '/powerBI/usage/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/usage/dashboard')
								// },
								// {
								// 	label: 'Data Growth', // <- added
								// 	path: '/powerBI/datagrowth/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/datagrowth/dashboard')
								// },
								// {
								// 	label: 'Infrastructure',
								// 	path: '/powerBI/infrastructure/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/infrastructure/dashboard')
								// },
								// {
								// 	label: 'Capacity Observability',
								// 	path: '/powerBI/capacityObservability/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/capacityObservability/dashboard')
								// },
								// {
								// 	label: 'Adoption Metrics',
								// 	path: '/powerBI/adoptionMetrics/dashboard',
								// 	activeMatch: (p) => p.includes('/powerBI/adoptionMetrics/dashboard')
								// },
								{
									label: 'Capacity',
									path: '/powerBI/userActivity/dashboard',
									activeMatch: (p) =>
										p.includes('/powerBI/userActivity/dashboard')
								},
								{
									label: 'Reports',
									path: '/powerBI/userActivity/reports',
									activeMatch: (p) =>
										p.includes('/powerBI/userActivity/reports')
								},
								{
									label: 'Users',
									path: '/powerBI/userActivity/users',
									activeMatch: (p) =>
										p.includes('/powerBI/userActivity/users')
								}
							]}
							// capabilities={capabilities} // enable if you want gating
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							// activeColorToken="text.focus"
							// inactiveColorToken="text.main"
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{/* DMF obs sidebar */}
			{(location.pathname === '/dataManagementFramework/healthSummary/dashboard' ||
				location.pathname === '/dataManagementFramework/Catalogues/dashboard') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Health Summary',
									path: '/dataManagementFramework/healthSummary/dashboard',
									activeMatch: (p) => p.includes('/dataManagementFramework/healthSummary/dashboard')
								},
								{
									label: 'Catalogues & Quality',
									path: '/dataManagementFramework/Catalogues/dashboard',
									activeMatch: (p) => p.includes('/dataManagementFramework/Catalogues/dashboard')
								}
							]}
							// capabilities={capabilities} // uncomment if you gate by capability
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							// activeColorToken="text.focus"
							// inactiveColorToken="text.main"
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{location.pathname?.includes('/verificationValidation') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Tableau',
								path: '/verificationValidation/dashboard',
								activeMatch: (p) => p.includes('/verificationValidation/dashboard')
							},
							{
								label: 'Power BI',
								path: '/verificationValidation/powerbi/dashboard',
								activeMatch: (p) => p.includes('/verificationValidation/powerbi/dashboard')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						// activeColorToken="text.focus"
						// inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/enterprisePlatform') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Platform',
								path: '/enterprisePlatform/dashboard',
								activeMatch: (p) => p.includes('/enterprisePlatform/dashboard')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/machineLearning') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Summary',
								path: '/machineLearning/dashboard',
								activeMatch: (p) => p.includes('/machineLearning')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/presto') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Query Performance Summary',
								path: '/presto/dashboard',
								activeMatch: (p) => p.includes('/presto/dashboard')
							}
						]}
						// capabilities={capabilities} // enable if you want gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/controlsCompliance') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Controls Testing Dashboard',
								path: '/controlsCompliance/dashboard',
								activeMatch: (p) => p.includes('/controlsCompliance/dashboard')
							}
						]}
						// capabilities={capabilities} // enable if you want capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/dataGrowth') && isSystemUserAllowed && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Dashboard',
								path: '/dataGrowth/dashboard',
								activeMatch: (p) => p.includes('/dataGrowth/dashboard')
							},
							{
								label: 'Power BI',
								path: '/dataGrowthDataGrowth/dashboard',
								activeMatch: (p) => p === '/dataGrowthDataGrowth/dashboard'
							},
							{
								label: 'Databricks',
								path: '/dataGrowthDatabricks/dashboard',
								activeMatch: (p) => p.includes('/dataGrowthDatabricks/dashboard')
							},
							{
								label: 'Presto',
								path: '/dataGrowthPresto/dashboard',
								activeMatch: (p) => p === '/dataGrowthPresto/dashboard'
							},
							{
								label: 'Tableau',
								path: '/dataGrowthTableau/dashboard',
								activeMatch: (p) => p === '/dataGrowthTableau/dashboard'
							},

						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{(location.pathname?.includes('/ibp/') && !location.pathname?.includes('/finops/resourcemapping/')) && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'IBP Pipelines',
								path: '/ibp/pipelines',
								activeMatch: (p) => p.includes('/ibp/pipelines')
							},
							{
								label: 'IBP Jobs Monitoring',
								path: '/ibp/dashboard',
								activeMatch: (p) => p.includes('/ibp/dashboard')
							},
							{
								label: 'IBP Executive Dashboard',
								path: '/ibp/executive',
								activeMatch: (p) => p.includes('/ibp/executive')
							},
							{
								label: 'BDR Dashboard',
								path: '/ibp/bdr-pbi',
								activeMatch: (p) => p.includes('/ibp/bdr-pbi')
							}
						]}
						// capabilities={capabilities} // uncomment if you want capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/mdip') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'MDIP Executive Dashboard',
								path: '/mdip/dashboard',
								activeMatch: (p) => p.includes('/mdip/dashboard')
							},
							{
								label: 'MDIP Job Dashboard',
								path: '/mdip/job',
								activeMatch: (p) => p.includes('/mdip/job')
							}
						]}
						// capabilities={capabilities} // uncomment if you need capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{/* IBP tabs (visible on any /ibp/* route) */}
			{isProgramExecutive && location.pathname?.includes('/ibp/') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'IBP Executive Dashboard',
								path: '/ibp/executive',
								activeMatch: (p) => p.includes('/ibp/executive'),
							},
							{
								label: 'BDR Dashboard',
								path: '/ibp/bdr-pbi',
								activeMatch: (p) => p.includes('/ibp/bdr-pbi'),
							},
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{/* Platform Cost (single tab; visible only on /dataestate/platform/cost) */}
			{isProgramExecutive && location.pathname?.includes('/dataestate/platform/cost') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Cost Dashboard',
								path: '/dataestate/platform/cost',
								activeMatch: (p) => p.includes('/dataestate/platform/cost'),
							},
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{/* Fin Ops tabs (visible on any of the FinOps pages) */}
			{isProgramExecutive && (
				location.pathname?.includes('/dataestate/financialOverview') ||
				location.pathname?.includes('/dataestate/financialProgram') ||
				location.pathname?.includes('/dataestate/financialDetails')
			) && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Overview',
									// original used <a href="/dataestate/financialOverview">
									// we’ll keep the same behavior via onTabClick
									path: '/dataestate/financialOverview',
									activeMatch: (p) => p.includes('/dataestate/financialOverview'),
								},
								{
									label: 'Program Summary',
									path: '/dataestate/financialProgram',
									activeMatch: (p) => p.includes('/dataestate/financialProgram'),
								},
								{
									label: 'Details Summary',
									path: '/dataestate/financialDetails',
									activeMatch: (p) => p.includes('/dataestate/financialDetails'),
								},
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							//   activeColorToken="text.focus"
							//   inactiveColorToken="text.main"
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{location.pathname?.includes('/databricksADB') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Workspace Overview',
								path: '/databricksADB/workspace',
								activeMatch: (p) => p.includes('/databricksADB/workspace')
							},
							{
								label: searchParams.get('tab') === '1' ? 'Peak Hour Analysis' : 'Job Analysis',
								path: searchParams.get('tab') === '1'
									? '/databricksADB/jobsADB?tab=1'
									: '/databricksADB/jobsADB?tab=0',
								activeMatch: (p) => p.includes('/databricksADB/jobsADB'),
								children: [
									{
										label: 'Job Analysis',
										path: '/databricksADB/jobsADB?tab=0',
										activeMatch: () =>
											new URLSearchParams(window.location.search).get('tab') !== '1'
									},
									{
										label: 'Peak Hour Analysis',
										path: '/databricksADB/jobsADB?tab=1',
										activeMatch: () =>
											new URLSearchParams(window.location.search).get('tab') === '1'
									}
								]
							},
							{
								label: 'Cluster Analytics',
								path: '/databricksADB/clusteranalytics',
								activeMatch: (p) => p.includes('/databricksADB/clusteranalytics')
							},
							{
								label: 'DBSQL Performance',
								path: '/databricksADB/dbsqlperfomance',
								activeMatch: (p) => p.includes('/databricksADB/dbsqlperfomance')
							},
							{
								label: 'Custom Analysis & Recommendations',
								path: '/databricksADB/customanalysis',
								activeMatch: (p) => p.includes('/databricksADB/customanalysis')
							},
							{
								label: 'Cost Management & Financial Analysis',
								path: '/databricksADB/costmanagement',
								activeMatch: (p) => p.includes('/databricksADB/costmanagement')
							},
						]}
						// capabilities={capabilities} // uncomment if you want capability-based gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						// activeColorToken="text.focus"
						// inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/dataestate') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Executive Summary',
								path: '/dataestate/executive-summary',
								activeMatch: (p) => p.includes('/dataestate/executive-summary')
							},
							{
								label: 'Storage',
								path: '/dataestate/storagesummary',
								activeMatch: (p) => p.includes('/dataestate/storagesummary')
							},
							{
								label: 'Query',
								path: '/dataestate/queriessummary',
								activeMatch: (p) => p.includes('/dataestate/queriessummary')
							},
							{
								label: 'Data Processing',
								path: '/dataestate/data-processing',
								activeMatch: (p) => p.includes('/dataestate/data-processing')
							},
							{
								label: 'BI Application',
								path: '/dataestate/biApplicationSummary',
								activeMatch: (p) => p.includes('/dataestate/biApplicationSummary')
							},
							{
								label: 'Major INC',
								path: '/dataestate/majorIncident',
								activeMatch: (p) => p.includes('/dataestate/majorIncident')
							}
						]}
						// capabilities={capabilities} // uncomment if you want capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
						rightContent={
							location.pathname?.includes('/dataestate/queriessummary') ||
								location.pathname?.includes('/dataestate/biApplicationSummary') || 
								location.pathname?.includes('/dataestate/majorIncident') ? (
								<Box sx={{ display: 'flex', alignItems: 'center', pr: 2, height: 24 }}>
									<DataEstateDateFilter
										customMonth={dataEstateCustomMonth}
										filterMonth={dataEstateFilterMonth}
										setCustomMonth={setDataEstateCustomMonth}
										setFilterMonth={setDataEstateFilterMonth}
										handleChange={handleDataEstateDateChange}
									/>
								</Box>
							) : undefined
						}
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}
			{location.pathname?.includes('/productCatalog') && (

				<>

					<HeaderTabs

						items={PRODUCT_CATALOG_TABS}

						// capabilities={capabilities} // uncomment if you want capability gating

						left={75}

						right={15}

						zIndex={10}

						topPadding={1}

						showDivider

						//   activeColorToken="text.focus"

						//   inactiveColorToken="text.main"

						fixed

					/>

					<Box sx={{ height: 56 }} />

				</>

			)}
			{location.pathname?.includes('/productSuccessDashboard') && (

				<>

					<HeaderTabs

						items={PRODUCT_SUCCESS_DASHBOARD_TABS}

						left={75}

						right={15}

						zIndex={10}

						topPadding={1}

						showDivider

						//   activeColorToken="text.focus"

						//   inactiveColorToken="text.main"

						fixed

					/>

					<Box sx={{ height: 56 }} />

				</>

			)}
			{location.pathname?.includes('/finops/resourcemapping') && (
				<>
					<Box
						sx={{
							position: 'fixed',
							left: 75,
							right: 35,
							zIndex: 10,
						}}
					>
						<HeaderTabs
							items={[
								{
									label: 'Summary',
									path: '/finops/resourcemapping/programs',
									activeMatch: (p) =>
										p.includes('/finops/resourcemapping/programs') ||
										p.includes('/finops/resourcemapping/projects') ||
										p.includes('/finops/resourcemapping/search-resources')
								},
								{
									label: 'Search & Allocate',
									path: '/finops/resourcemapping/search',
									activeMatch: (p) => p === '/finops/resourcemapping/search'
								},
								...(checkRole(role, FINOPS_EXEC_COST_ROLES) ? [{
									label: 'Executive Cost',
									path: '/finops/resourcemapping/costsummary',
									activeMatch: (p: string) => p.includes('/finops/resourcemapping/costsummary'),
									children: [
										{
											label: 'Executive Cost',
											path: '/finops/resourcemapping/costsummary',
											activeMatch: (p: string) => p === ('/finops/resourcemapping/costsummary')
										},
										{
											label: 'Sector Cost',
											path: '/finops/resourcemapping/costsummary/sector',
											activeMatch: (p: string) => p === ('/finops/resourcemapping/costsummary/sector')
										},
										{
											label: 'Program Cost',
											path: '/finops/resourcemapping/costsummary/program',
											activeMatch: (p: string) => p === ('/finops/resourcemapping/costsummary/program')
										},
										{
											label: 'Project Cost',
											path: '/finops/resourcemapping/costsummary/project',
											activeMatch: (p: string) => p === ('/finops/resourcemapping/costsummary/project')
										}
									]
								}] : [])
							]}
							left={0}
							right={0}
							zIndex={10}
							topPadding={1}
							showDivider
							//   activeColorToken="text.focus"
							//   inactiveColorToken="text.main"
							fixed={false}
							disabled={isDetailViewActive}
						/>
						{(location.pathname?.includes('/finops/resourcemapping/programs') || location.pathname?.includes('/finops/resourcemapping/projects')) && (
							<Box
								sx={{
									position: 'absolute',
									right: 12,
									top: '51%',
									transform: 'translateY(-50%)',
									zIndex: 11,
									display: 'flex',
									alignItems: 'center',
									height: 24,
								}}
							>
								{(() => {
									const isProgramDetails = location.pathname.startsWith('/finops/resourcemapping/programs/');
									const isProjectDetails = location.pathname.startsWith('/finops/resourcemapping/projects/');
									let eventName = 'programsSearch';
									let searchType = 'programs';
									if (isProgramDetails) {
										eventName = 'programDetailsSearch';
										searchType = 'projects';
									} else if (isProjectDetails) {
										eventName = 'projectDetailsSearch';
										searchType = 'resources';
									}
									return (
										<SearchField
											value={globalSearch.searchText}
											type={searchType}
											onChange={(val) => {
												globalSearch.setSearchText(val);
												const searchKeyword = val.toLowerCase();
												if (searchKeyword.length >= 3 || !searchKeyword) {
													globalThis.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: searchKeyword } }));
												}
											}}
											onSearch={() => {
												globalSearch.setIsSearching(true);
												globalThis.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: globalSearch.searchText.toLowerCase() } }));
												setTimeout(() => globalSearch.setIsSearching(false), 500);
											}}
											onClear={() => {
												globalSearch.setIsClearingIcon(true);
												globalSearch.setSearchText('');
												globalThis.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: '' } }));
												setTimeout(() => globalSearch.setIsClearingIcon(false), 500);
											}}
											searchIconSize={24}
											otherIconsSize={18}
											isSearching={globalSearch.isSearching}
											isClearingIcon={globalSearch.isClearingIcon}
										/>
									);
								})()}
							</Box>
						)}
					</Box>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/platformCostObs/dashboard') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Platform Cost',
								path: '/platformCostObs/dashboard',
								activeMatch: (p) => p.includes('/platformCostObs/dashboard')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{(location.pathname?.includes('/data-pipeline/programs') ||
				location.pathname?.includes('/data-pipeline/projects') ||
				location.pathname?.includes('/data-pipeline/search-jobs')) && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Programs',
									path: '/data-pipeline/dataOps',
									activeMatch: (p) =>
										p.includes('/data-pipeline/programs') ||
										p.includes('/data-pipeline/projects') ||
										p.includes('/data-pipeline/search-jobs')
								}
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							//   activeColorToken="text.focus"
							//   inactiveColorToken="text.main"
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{location.pathname?.includes('/data-pipeline/dataOps') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Programs',
								path: '/data-pipeline/dataOps',
								activeMatch: (p) => p.includes('/data-pipeline/dataOps')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					{/* spacer so content isn't hidden under the fixed tabs */}
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/data-pipeline/executiveTemplate') && (
				<>
					{(() => {
						const sectorLabel = searchParams.get('sector');
						let headerPath = 'dataOps';
						if (sectorLabel) {
							if (sectorLabel === 'pbna' || sectorLabel === 'apac'
								|| sectorLabel === 'flna' || sectorLabel === 'pfna') {
								headerPath = 'executiveOps';
							}
						}
						return (
							<HeaderTabs
								items={[
									{
										label: sectorLabel ? 'Sector Executive Dashboard' : 'Programs',
										path: `/data-pipeline/${headerPath}`,
										activeMatch: (p) => p.includes('/data-pipeline/executiveTemplate')
									}
								]}
								left={75}
								right={15}
								zIndex={10}
								topPadding={1}
								showDivider
								//   activeColorToken="text.focus"
								//   inactiveColorToken="text.main"
								fixed
							/>
						);
					})()}

					{/* Add spacing to prevent content from overlapping the fixed tabs */}
					<Box sx={{ height: 56 }} />
				</>
			)}

			{
				location.pathname?.includes('/D&AIXOps/Summary') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Summary',
									path: '/D&AIXOps/Summary',
									activeMatch: (p) => p.includes('/D&AIXOps/Summary')
								}
							]}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/experience') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Happy Signals',
									path: '/D&AIXOps/experience/HappySignals',
									activeMatch: (p) => p.includes('/D&AIXOps/experience/HappySignals')
								},
							]}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/itsm') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'MIM',
									path: '/D&AIXOps/itsm/MIMReports',
									activeMatch: (p) => p.includes('/D&AIXOps/itsm/MIMReports')
								},
							]}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/segments') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Department Ops',
									path: '/D&AIXOps/segments/DepartmentMetrics',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/DepartmentMetrics')
								},
								{
									label: 'Sector Ops',
									path: '/D&AIXOps/segments/SectorMetrics',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/SectorMetrics')
								},
								{
									label: 'Capability',
									path: '/D&AIXOps/segments/capability/pipelineMatrics',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/capability'),
									children: [
										{
											label: 'Pipeline Ops',
											path: '/D&AIXOps/segments/capability/pipelineMatrics',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/pipelineMatrics')
										},
										{
											label: 'Integration Ops',
											path: '/D&AIXOps/segments/capability/integration-ops',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/integration-ops')
										},
										{
											label: 'Sector Data Lake Ops',
											path: '/D&AIXOps/segments/capability/sector-data-lake',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/sector-data-lake')
										},
										{
											label: 'Platform Ops',
											path: '/D&AIXOps/segments/capability/platformOpsMatrics',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/platformOpsMatrics')
										},
										{
											label: 'BI Ops',
											path: '/D&AIXOps/segments/capability/DepartmentLevelMetrics',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/DepartmentLevelMetrics')
										},
										{
											label: 'AI/ML Ops',
											path: '/D&AIXOps/segments/capability/AIMLMetrics',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/AIMLMetrics')
										},
										{
											label: 'DMO',
											path: '/D&AIXOps/segments/capability/DMOMetrics',
											activeMatch: (p) => p.includes('/D&AIXOps/segments/capability/DMOMetrics')
										},
									]
								},
								{
									label: 'Governance',
									path: '/D&AIXOps/segments/governance',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/governance')
								},
								{
									label: 'Program',
									path: '/D&AIXOps/segments/programMetrics',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/programMetrics')
								},
								{
									label: 'Apps & Products',
									path: '/D&AIXOps/segments/Apps-Products',
									activeMatch: (p) => p.includes('/D&AIXOps/segments/Apps-Products'),
								},
							]}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				(decodeURIComponent(location.pathname || '').includes('/finops/executive') || location.pathname === '/finops/executive-dashboard' || location.pathname.includes('/finopsMetrics/insightView') || location.pathname.includes('/finopsExecutive/cloud-spend-report') || location.pathname.includes('/finopsExecutive/pre-aop-2027') || location.pathname.includes('/finopsExecutive/edf-cost-overview') || location.pathname.includes('/finopsExecutive/2026-dissection') || location.pathname.includes('/finopsExecutive/edf-cost-overview') || location.pathname.includes('/finopsExecutive/2026-dissection')) && (
					<>
						<HeaderTabs
							items={[
								...(checkRole(role, FINOPS_USER_ROLES) ? [
									{ label: 'Executive Summary', path: '/finops/executive', activeMatch: () => { const t = new URLSearchParams(window.location.search).get('tab'); const p = window.location.pathname; return p !== '/finops/executive-dashboard' && !p.includes('/finopsMetrics/insightView') && !p.includes('/finopsExecutive/cloud-spend-report') && !p.includes('/finopsExecutive/pre-aop-2027') && !p.includes('/finopsExecutive/edf-cost-overview') && !p.includes('/finopsExecutive/2026-dissection') && (!t || t === '0'); } },
									{ label: 'Cost', path: '/finops/executive?tab=1', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '1' },
									{ label: 'FinOps', path: '/finops/executive?tab=2', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '2' || window.location.pathname === '/finops/executive-dashboard' || window.location.pathname.includes('/finopsMetrics/insightView') },
								] : []),
								...(checkRole(role, FINOPS_EXEC_COST_ROLES) ? [{
									label: 'Executive Cost',
									path: '/finops/executive?tab=3',
									activeMatch: () => ['3', '4', '5', '6'].includes(new URLSearchParams(window.location.search).get('tab') ?? ''),
									children: [
										{ label: 'Executive Cost', path: '/finops/executive?tab=3', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '3' },
										{ label: 'Sector Cost', path: '/finops/executive?tab=4', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '4' },
										{ label: 'Program Cost', path: '/finops/executive?tab=5', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '5' },
										{ label: 'Project Cost', path: '/finops/executive?tab=6', activeMatch: () => new URLSearchParams(window.location.search).get('tab') === '6' },
									],
								}] : []),
								...(checkRole(role, FINOPS_ALL_ROLES) ? [
									{
										label: 'Cloud Spend',
										path: '/finopsExecutive/cloud-spend-report',
										activeMatch: (p: string) => p === '/finopsExecutive/cloud-spend-report'
									},
									{
										label: 'EDF Cost Overview',
										path: '/finopsExecutive/edf-cost-overview',
										activeMatch: (p: string) => p === '/finopsExecutive/edf-cost-overview'
									},
									{
										label: 'Pre-AOP 2027',
										path: '/finopsExecutive/pre-aop-2027',
										activeMatch: (p: string) => p === '/finopsExecutive/pre-aop-2027'
									},
									{
										label: '2026 Dissection',
										path: '/finopsExecutive/2026-dissection',
										activeMatch: (p: string) => p === '/finopsExecutive/2026-dissection'
									}
								] : []),
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							fixed
							rightContent={finopsShowControls ? (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 15, height: 24 }}>
									{finopsShowDateRefresh && (
										<DateTimeRangeFilter
											dateRangeType={finopsDateRangeType}
											customStartDate={finopsCustomStartDate}
											customEndDate={finopsCustomEndDate}
											onChange={handleFinOpsDateRangeChange}
											timezone={'CST'}
										/>
									)}
								</Box>
							) : undefined}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/comp-inv') && (
					<>
						<HeaderTabs items={[
							{
								label: 'Governance',
								path: '/D&AIXOps/comp-inv/governance',
								activeMatch: (p) => p.includes('/D&AIXOps/comp-inv/governance')
							}
						]} />
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/misc') && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Topic of the Month',
									path: '/D&AIXOps/misc/topicOfTheMonth',
									activeMatch: (p) => p.includes('/D&AIXOps/misc/topicOfTheMonth')
								},
								{
									label: 'D&AI Risk Page',
									path: '/D&AIXOps/misc/riskpage',
									activeMatch: (p) => p.includes('/D&AIXOps/misc/riskpage')
								},
								{
									label: 'I&I Dashboards',
									path: '/D&AIXOps/misc/npiDashboards',
									activeMatch: (p) => p.includes('/D&AIXOps/misc/npiDashboards')
								},
								{
									label: 'D&AI CCO Insights - Hub',
									path: '/D&AIXOps/misc/vendorManagement',
									activeMatch: (p) => p.includes('/D&AIXOps/misc/vendorManagement')
								}
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{
				location.pathname?.includes('/D&AIXOps/service') && (
					<>
						<HeaderTabs
							items={[
								{
									label: (
										<Tooltip title="Business Application Intelligence Center" placement="bottom" arrow>
											<span>BAI Center</span>
										</Tooltip>
									) as any,
									path: '/D&AIXOps/service/BAICenter',
									activeMatch: (p) => p.includes('/D&AIXOps/service/BAICenter')
								},
								{
									label: (
										<Tooltip title="Incident Insight Intelligence Work Notes Quality Hub" placement="bottom" arrow>
											<span>KORE I³ Hub</span>
										</Tooltip>
									) as any,
									path: '/D&AIXOps/service/KOREIHub',
									activeMatch: (p) => p.includes('/D&AIXOps/service/KOREIHub')
								},
								{
									label: (
										<Tooltip title="Operational - Incident Intelligence Center" placement="bottom" arrow>
											<span>OPS I² Center</span>
										</Tooltip>
									) as any,
									path: '/D&AIXOps/service/OPSICenter',
									activeMatch: (p) => p.includes('/D&AIXOps/service/OPSICenter')
								},
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)
			}
			{location.pathname?.includes('/DAIXOps') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Overview',
								path: '/DAIXOps/summary/overview',
								activeMatch: (p) =>
									p.includes('/DAIXOps/summary/overview')
							},
							{
								label: 'D&AI Risk Page',
								path: '/D&AIXOps/riskPage', // A new path for the risk page
								activeMatch: (p) => p.includes('/D&AIXOps/riskPage')
							},
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{(location.pathname?.includes('/alertFramework/alert') ||
				location.pathname?.includes('/alertFramework/createalert') ||
				location.pathname?.includes('/alert-details') ||
				location.pathname?.includes('/kpiFramework')) && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Alerts',
									path: '/alertFramework/alert',
									activeMatch: (p) =>
										p.includes('/alertFramework/alert') ||
										p.includes('/alertFramework/createalert') ||
										p.includes('/alert-details')
								},
								{
									label: 'KPI Store',
									path: '/kpiFramework/configs',
									activeMatch: (p) => p.includes('/kpiFramework')
								}
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							//   activeColorToken="text.focus"
							//   inactiveColorToken="text.main"
							fixed
							rightContent={
								location.pathname?.includes('/kpiFramework/configs/') && !location.pathname?.includes('/kpiFramework/configs/new') ? (
									<Box sx={{ display: 'flex', alignItems: 'center', pr: 2, height: 24 }}>
										<DataEstateDateFilter
											customMonth={dataEstateCustomMonth}
											filterMonth={dataEstateFilterMonth}
											setCustomMonth={setDataEstateCustomMonth}
											setFilterMonth={setDataEstateFilterMonth}
											handleChange={handleDataEstateDateChange}
										/>
									</Box>
								) : undefined
							}
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{location.pathname?.includes('/data-pipeline/programOps') && !location.pathname?.includes('/data-pipeline/programOps/IBP/markets/ibpDashboard') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Program Ops Dashboards',
								path: '/data-pipeline/programOpsDashboard',
								activeMatch: (p) => p.includes('/data-pipeline/programOps')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/data-pipeline/executiveOps/sector') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Sector Dashboards',
								path: '/data-pipeline/executiveOps/sector',
								activeMatch: (p) => p.includes('/data-pipeline/executiveOps/sector')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/data-pipeline/executiveOps/program') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Program Dashboards',
								path: '/data-pipeline/executiveOps/program',
								activeMatch: (p) => p.includes('/data-pipeline/executiveOps/program')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{location.pathname?.includes('/data-pipeline/executiveOps/pepsellPepIrisRecon') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Transaction Data',
								path: getDataReconTabPath('transactional'),
								activeMatch: (p) => p.includes('/data-pipeline/executiveOps/pepsellPepIrisRecon') && selectedDataReconTab === 'transactional'
							},
							{
								label: 'Master Data',
								path: getDataReconTabPath('master'),
								activeMatch: (p) => p.includes('/data-pipeline/executiveOps/pepsellPepIrisRecon') && selectedDataReconTab === 'master'
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
						rightContent={
							<Box sx={{ display: 'flex', alignItems: 'center', pr: 2, height: 24 }}>
								<DateTimeRangeFilter
									dateRangeType={dateRangeType}
									customStartDate={customStartDate}
									customEndDate={customEndDate}
									onChange={handleDateRangeChange}
									timezone="CST"
									fixedOptions={DATA_RECON_FIXED_OPTIONS}
								/>
							</Box>
						}
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}
			
			{location.pathname?.includes('/credsManagement/summary') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Summary',
								path: '/credsManagement/summary',
								activeMatch: (p) => p.includes('/credsManagement/summary')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}

			{!location.pathname?.includes('platformCostObs') &&
				location.pathname !== DataGrowthDashboard &&
				location.pathname !== '/databricks/summary/dashboard' &&
				location.pathname !== '/databricks/resource/dashboard' &&
				location.pathname !== '/databricks/jobs/dashboard' &&
				location.pathname !== '/databricks/costSummary/dashboard' &&
				location.pathname !== '/databricks/governance/dashboard' &&
				location.pathname !== '/databricks/infrastructure/dashboard' &&
				// location.pathname !== '/powerBI/capacity/dashboard' &&
				// location.pathname !== '/powerBI/capacityObservability/dashboard' &&
				// location.pathname !== '/powerBI/gatewayObservability/dashboard' &&
				// location.pathname !== '/powerBI/adoptionMetrics/dashboard' &&
				// location.pathname !== '/powerBI/dataset/dashboard' &&
				// location.pathname !== '/powerBI/usage/dashboard' &&
				// location.pathname !== '/powerBI/datagrowth/dashboard' &&
				// location.pathname !== '/powerBI/infrastructure/dashboard' &&
				location.pathname !== '/dataManagementFramework/healthSummary/dashboard' &&
				location.pathname !== '/dataManagementFramework/Catalogues/dashboard' &&
				!location.pathname?.includes('/tableau') &&
				!location.pathname?.includes('/machineLearning') &&
				!location.pathname?.includes('/verificationValidation') &&
				!location.pathname?.includes('/presto') &&
				!location.pathname?.includes('/controlsCompliance') &&
				!location.pathname?.includes('/ibp') &&
				!location.pathname?.includes('/dataGrowth') &&
				!location.pathname?.includes('/dataestate') &&
				capabilities?.includes('Admin') &&
				!location.pathname?.includes('/databricks') &&
				!location.pathname?.includes('/finops') &&
				!location.pathname?.includes('/data-pipeline') &&
				role == 'Admin' && (
					<>
						<HeaderTabs
							items={[
								{
									label: 'Sectors',
									path: '/master/sectors',
									activeMatch: (p) => p === '/master/sectors'
								},
								{
									label: 'Systems',
									path: '/master/systems',
									activeMatch: (p) => p === '/master/systems'
								},
								{
									label: 'Processes',
									path: '/master/processes',
									activeMatch: (p) => p === '/master/processes'
								},
								{
									label: 'Reasons Of SLA Not Met',
									path: '/master/reasonsOfSLA',
									activeMatch: (p) => p === '/master/reasonsOfSLA'
								}
							]}
							left={75}
							right={15}
							zIndex={10}
							topPadding={1}
							showDivider
							// activeColorToken="text.focus"
							// inactiveColorToken="text.main"
							fixed
						/>
						<Box sx={{ height: 56 }} />
					</>
				)}

			{location.pathname?.includes('/dataops') && (
				<>
					<Box
						sx={{
							position: 'fixed',
							left: 75,
							right: 15,
							top: 30,
							zIndex: 10,
							pt: 1,
						}}
					>
						<HeaderTabs
							items={[
								{
									label: 'Pipeline Early Warning System',
									path: '/dataops/unified-alerts',
									activeMatch: (p) => p.includes('/dataops/unified-alerts'),
								},
								{
									label: 'Ops Pulse Dashboard',
									path: '/dataops/unified-incidents',
									activeMatch: (p) => p.includes('/dataops/unified-incidents'),
								},
							]}
							left={0}
							right={0}
							zIndex={10}
							topPadding={0}
							showDivider
							fixed={false}
							rightContent={
								!location.pathname?.includes('dataops/early-warning-rule') && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											height: 24,
											whiteSpace: 'nowrap',
										}}
									>
										<SearchField
											value={globalSearch.searchText}
											type={location.pathname?.includes('/dataops/unified-alerts') ? 'alerts' : 'ops pulse'}
											onChange={(val: string) => {
												globalSearch.setSearchText(val);
												// Manually clearing the box (backspace to empty) should drop the applied
												// filter too, same as the explicit clear icon, so a visibly empty box never
												// leaves a stale search silently active.
												if (!val && globalSearch.searchText) {
													const eventName = location.pathname?.includes('/dataops/unified-alerts') ? 'alertsSearch' : 'incidentsSearch';
													window.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: '' } }));
												}
											}}
											onSearch={() => {
												globalSearch.setIsSearching(true);
												const eventName = location.pathname?.includes('/dataops/unified-alerts') ? 'alertsSearch' : 'incidentsSearch';
												window.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: globalSearch.searchText } }));
												setTimeout(() => globalSearch.setIsSearching(false), 500);
											}}
											onClear={() => {
												globalSearch.setIsClearingIcon(true);
												globalSearch.setSearchText('');
												const eventName = location.pathname?.includes('/dataops/unified-alerts') ? 'alertsSearch' : 'incidentsSearch';
												window.dispatchEvent(new CustomEvent(eventName, { detail: { searchText: '' } }));
												setTimeout(() => globalSearch.setIsClearingIcon(false), 500);
											}}
											searchIconSize={24}
											otherIconsSize={18}
											isSearching={globalSearch.isSearching}
											isClearingIcon={globalSearch.isClearingIcon}
										/>
										<Box sx={{ mx: 1 }} />
										<AutoRefresh
											currentInterval={autoRefreshInterval}
											onIntervalChange={handleAutoRefreshChange}
										/>
										<Box sx={{ mx: 1 }} />
										<DateTimeRangeFilter
											dateRangeType={pewsDateRangeType}
											customStartDate={pewsCustomStartDate}
											customEndDate={pewsCustomEndDate}
											onChange={handlePewsDateRangeChange}
											timezone={'CST'}
											fixedOptions={OPS_PULSE_FIXED_OPTIONS}
											olderThan90DaysMode
										/>
									</Box>
								)
							}
						/>
					</Box>
					<Box sx={{ height: 56 }} />
				</>
			)}
			{location.pathname?.includes('platformHealth/summary') && (
				<>
					<Box
						sx={{
							position: 'fixed',
							left: { xs: 0, md: 75 },
							right: { xs: 0, md: 15 },
							zIndex: 10,
							display: 'flex',
							flexWrap: { xs: 'wrap', md: 'nowrap' },
							alignItems: 'center',
							height: { xs: 'auto', md: 36 },
							overflow: 'visible',
							backgroundColor: 'Header.TabPagerHeaderBg',
							borderBottom: '1px solid rgba(0,0,0,0.08)',
						}}
					>
						<Typography
							variant="tabPageHeader"
							sx={{
								flexShrink: 0,
								px: 1.5,
								borderBottom: `3px solid`,
								borderColor: 'textLight.dark',
								height: 36,
								display: 'flex',
								alignItems: 'center',
								color: 'textLight.dark',
								cursor: 'default',
							}}
						>
							Summary
						</Typography>
						<Box
							id="platform-health-metrics-portal"
							sx={{
								flex: 1,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								minHeight: 30,
								minWidth: 0,
								overflow: 'visible',
								pr: 1,
								py: { xs: 0.5, md: 0 },
							}}
						/>
					</Box>
					<Box sx={{ height: { xs: 70, md: 40 } }} />
				</>
			)}

			{location.pathname?.includes('/demo-dashboard/AdvanceFilters') && (
				<>
					<HeaderTabs
						items={[
							{
								label: 'Filters Demo',
								path: '/demo-dashboard/AdvanceFilters',
								activeMatch: (p) => p.includes('/demo-dashboard/AdvanceFilters')
							},
						]}
						// capabilities={capabilities} // uncomment if you want capability gating
						left={75}
						right={15}
						zIndex={10}
						topPadding={1.5}
						showDivider
						//   activeColorToken="text.focus"
						//   inactiveColorToken="text.main"
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</>
			)}
			{location.pathname?.includes('/dataOps/integratedJobMonitoring') && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						top: 36,
					}}
				>
					<HeaderTabs
						items={[
							{
								label: 'Job Summary',
								path: '/dataOps/integratedJobMonitoring',
								activeMatch: (p) => p === '/dataOps/integratedJobMonitoring'
							},
							{
								label: 'Data Assets',
								path: '/dataOps/integratedJobMonitoring/dataAssets',
								activeMatch: (p: string) => p === '/dataOps/integratedJobMonitoring/dataAssets'
							},
							...(isMetadataUpdatesAllowed
								? [{
									label: 'Pipeline Metadata',
									path: '/dataOps/integratedJobMonitoring/metadataUpdates',
									activeMatch: (p: string) => p.includes('/dataOps/integratedJobMonitoring/metadataUpdates')
								}]
								: [])
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box
						sx={{
							position: 'absolute',
							right: 52,
							top: '39%',
							transform: 'translateY(-50%)',
							zIndex: 11,
							display: 'flex',
							alignItems: 'center',
							height: 24,
						}}
					>
						{!isMetadataUpdatesPage && (
							<>
								<SearchField
									value={globalSearch.searchText}
									type="jobs"
									onChange={globalSearch.setSearchText}
									onSearch={() => {
										globalSearch.setIsSearching(true);
										window.dispatchEvent(new CustomEvent('pipelineSearch', { detail: { searchText: globalSearch.searchText } }));
										setTimeout(() => globalSearch.setIsSearching(false), 500);
									}}
									onClear={() => {
										globalSearch.setIsClearingIcon(true);
										globalSearch.setSearchText('');
										window.dispatchEvent(new CustomEvent('pipelineSearch', { detail: { searchText: '' } }));
										setTimeout(() => globalSearch.setIsClearingIcon(false), 500);
									}}
									searchIconSize={24}
									otherIconsSize={18}
									isSearching={globalSearch.isSearching}
									isClearingIcon={globalSearch.isClearingIcon}
								/>
								<Box sx={{ mx: 1 }} />
								<CommonAutoRefresh
									autoRefreshInterval={autoRefreshPipelineInterval}
									onIntervalChange={handlePipelineAutoRefreshChange}
								/>
								<Box sx={{ mx: 1 }} />
								<DateTimeRangeFilter
									dateRangeType={dateRangeType}
									customStartDate={customStartDate}
									customEndDate={customEndDate}
									onChange={handleDateRangeChange}
									timezone={'CST'}
								/>
							</>)}
					</Box>
					<Box sx={{ height: 56 }} />
				</Box>
			)}

			{location.pathname?.includes('/data-pipeline/programOps/IBP/markets/ibpDashboard') && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						top: 36,
					}}
				>
					<HeaderTabs
						items={[
							{
								label: 'GDO Summary',
								path: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
								activeMatch: (p) => p === '/data-pipeline/programOps/IBP/markets/ibpDashboard'
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box
						sx={{
							position: 'absolute',
							right: 52,
							top: '39%',
							transform: 'translateY(-50%)',
							zIndex: 11,
							display: 'flex',
							alignItems: 'center',
							height: 24,
						}}
					>
						<Box sx={{ mx: 1 }} />
						<CommonAutoRefresh
							autoRefreshInterval={autoRefreshPipelineInterval}
							onIntervalChange={handlePipelineAutoRefreshChange}
						/>
						<Box sx={{ mx: 1 }} />
						<DateTimeRangeFilter
							dateRangeType={dateRangeType}
							customStartDate={customStartDate}
							customEndDate={customEndDate}
							onChange={handleDateRangeChange}
							timezone={'CST'}
						/>

					</Box>
					<Box sx={{ height: 56 }} />
				</Box>
			)}

			{location.pathname?.includes('/data-pipeline/ibpDashboard/gdo-details') && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						top: 36,
					}}
				>
					<HeaderTabs
						items={[
							{
								label: 'GDO Summary',
								path: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
								activeMatch: (p: string) => p.includes('/data-pipeline/ibpDashboard/gdo-details')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box
						sx={{
							position: 'absolute',
							right: 52,
							top: '39%',
							transform: 'translateY(-50%)',
							zIndex: 11,
							display: 'flex',
							alignItems: 'center',
							height: 24,
						}}
					>
						<Box sx={{ mx: 1 }} />
						<CommonAutoRefresh
							autoRefreshInterval={autoRefreshPipelineInterval}
							onIntervalChange={handlePipelineAutoRefreshChange}
						/>
						<Box sx={{ mx: 1 }} />
						<DateTimeRangeFilter
							dateRangeType={dateRangeType}
							customStartDate={customStartDate}
							customEndDate={customEndDate}
							onChange={handleDateRangeChange}
							timezone={'CST'}
						/>

					</Box>
					<Box sx={{ height: 56 }} />
				</Box>
			)}

			{location.pathname?.includes('/data-pipeline/ibpDashboard/stage-details') && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						top: 36,
					}}
				>
					<HeaderTabs
						items={[
							{
								label: 'GDO Summary',
								path: '/data-pipeline/programOps/IBP/markets/ibpDashboard',
								activeMatch: (p: string) => p.includes('/data-pipeline/ibpDashboard/stage-details')
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box
						sx={{
							position: 'absolute',
							right: 52,
							top: '39%',
							transform: 'translateY(-50%)',
							zIndex: 11,
							display: 'flex',
							alignItems: 'center',
							height: 24,
						}}
					>
						<Box sx={{ mx: 1 }} />
						<CommonAutoRefresh
							autoRefreshInterval={autoRefreshPipelineInterval}
							onIntervalChange={handlePipelineAutoRefreshChange}
						/>
						<Box sx={{ mx: 1 }} />
						<DateTimeRangeFilter
							dateRangeType={dateRangeType}
							customStartDate={customStartDate}
							customEndDate={customEndDate}
							onChange={handleDateRangeChange}
							timezone={'CST'}
						/>

					</Box>
					<Box sx={{ height: 56 }} />
				</Box>
			)}

			{location.pathname?.includes('/data-pipeline/ibpDashboard/gdoTabs') && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						top: 36,
					}}
				>
					<HeaderTabs
						items={[
							{
								label: 'Summary',
								path: '/data-pipeline/ibpDashboard/gdoTabs',
								activeMatch: (p) => p === '/data-pipeline/ibpDashboard/gdoTabs'
							}
						]}
						left={75}
						right={15}
						zIndex={10}
						topPadding={1}
						showDivider
						fixed
					/>
					<Box sx={{ height: 56 }} />
				</Box>
			)}

			{location.pathname?.includes('/data-pipeline/ibp-dashboard/programs') && (!location.pathname?.includes('/data-pipeline/ibp-dashboard/programs-tab')) && (
				<Box
					sx={{
						position: 'fixed',
						left: 75,
						right: 15,
						zIndex: 10,
						// pt: 1,
					}}
				>
					<HeaderTabs
						items={[
							// {
							// 	label: 'Summary',
							// 	path: '/dataOps/pipelineRunStatus',
							// 	activeMatch: (p) => p.includes('/dataOps/pipelineRunStatus')
							// },
							{
								label: 'Summary',
								path: '/data-pipeline/ibp-dashboard/programs',
								activeMatch: (p) => p.includes('/data-pipeline/ibp-dashboard/programs')
							}
						]}
						left={0}
						right={0}
						zIndex={10}
						topPadding={0}
						showDivider
						fixed={false}
					/>
					<Box
						sx={{
							position: 'absolute',
							right: 12,
							top: '50%',
							transform: 'translateY(-50%)',
							zIndex: 11,
							display: 'flex',
							alignItems: 'center',
							height: 24,
						}}
					>
						<CommonAutoRefresh
							autoRefreshInterval={autoRefreshPipelineInterval}
							onIntervalChange={handlePipelineAutoRefreshChange}
						/>
						<Box sx={{ mx: 1 }} />
						<DateTimeRangeFilter
							dateRangeType={dateRangeType}
							customStartDate={customStartDate}
							customEndDate={customEndDate}
							onChange={handleDateRangeChange}
							timezone={'CST'}
						/>
					</Box>
				</Box>
			)}          {location.pathname?.includes('/dataOps/crossFlowNavigator') && (
				<>
					<Box
						sx={{
							position: 'fixed',
							left: 75,
							right: 15,
							zIndex: 10,
							// pt: 1,
						}}
					>
						<HeaderTabs
							items={[
								{
									label: 'Lineage Map',
									path: '/dataOps/crossFlowNavigator/lineage',
									activeMatch: (p) => p.endsWith('/lineage') || p === '/dataOps/crossFlowNavigator' || p === '/dataOps/crossFlowNavigator/'
								},
								/* {
									label: 'Reports',
									path: '/dataOps/crossFlowNavigator/reports',
									activeMatch: (p) => p.endsWith('/reports')
								}, */
								/* {
									label: 'Cluster Panel',
									path: '/dataOps/crossFlowNavigator/clusterPanel',
									activeMatch: (p) => p.endsWith('/clusterPanel')
								},
								{
									label: 'Admin',
									path: '/dataOps/crossFlowNavigator/admin',
									activeMatch: (p) => p.endsWith('/admin')
								} */
							]}
							left={0}
							right={0}
							zIndex={10}
							topPadding={1}
							showDivider
							fixed={false}
						/>
						<Box
							id="crossflow-portal"
							sx={{
								position: 'absolute',
								right: 12,
								top: '18px',
								transform: 'translateY(-50%)',
								zIndex: 11,
								display: 'flex',
								alignItems: 'center',
								gap: '16px'
							}}
						/>
					</Box>
					<Box sx={{ height: 56 }} />
				</>
			)}
		</>
	);
};

export default TabPageHeader;
